import { Tooltip, TooltipTrigger } from '@vizit/atoms';
import { XCloseIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { Button as AriaButton, Toolbar } from 'react-aria-components';

interface BulkAction {
  /**
   * Stable key for the action.
   *
   * @required
   */
  key: string;
  /**
   * The button label.
   *
   * @required
   */
  label: string;
  /**
   * The icon before the label, sized 15px; it takes the button's text color.
   *
   * @required
   */
  icon: React.ReactNode;
  /**
   * Runs the action.
   *
   * @required
   */
  onPress: () => void;
  /**
   * Whether the action is unavailable for the current selection.
   *
   * @optional
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Tooltip explaining the action, e.g. "Compare 3 assets side by side".
   *
   * @optional
   */
  title?: string;
  /**
   * Tooltip shown instead of `title` while disabled, explaining why, e.g. "Select at least two assets to compare".
   *
   * @optional
   */
  disabledReason?: string;
  /**
   * `danger` turns the button red on hover, for destructive actions like Delete. It stays neutral at rest.
   *
   * @optional
   * @default 'default'
   */
  tone?: 'default' | 'danger' | 'primary';
}

interface BulkActionBarProps {
  /**
   * How many items are selected. The bar renders only while this is above zero.
   *
   * @required
   */
  count: number;
  /**
   * The actions, in display order.
   *
   * @required
   */
  actions: BulkAction[];
  /**
   * Clears the selection; called by the × button and by Esc.
   *
   * @required
   */
  onClear: () => void;
  /**
   * Accessible name for the toolbar.
   *
   * @optional
   * @default 'Bulk actions'
   */
  'aria-label'?: string;
  /**
   * @optional
   */
  className?: string;
}

/** Inputs the user types into. Checkboxes and sliders aren't here: Esc from a tile checkbox should clear. */
const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'url', 'tel', 'password', 'number']);

/** Whether a key event comes from somewhere the user is typing, where Esc belongs to the field. */
function isEditableTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable ||
      target.tagName === 'TEXTAREA' ||
      (target instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(target.type)))
  );
}

/** Shared by the action and clear buttons: square, no border, white focus outline inside the button. */
const buttonBaseClasses = cx(
  'h-16 cursor-pointer outline-none',
  'data-[focus-visible]:outline-2 data-[focus-visible]:outline data-[focus-visible]:outline-white',
  'data-[focus-visible]:-outline-offset-2',
);

function BulkActionButton(props: Omit<BulkAction, 'key'>) {
  const { label, icon, onPress, isDisabled = false, title, disabledReason, tone = 'default' } = props;
  const reasonId = React.useId();
  const showReason = isDisabled && Boolean(disabledReason);
  const tooltip = isDisabled ? (disabledReason ?? title) : title;
  const keepsFocusForTooltip = isDisabled && Boolean(tooltip);

  const button = (
    <AriaButton
      onPress={isDisabled ? undefined : onPress}
      isDisabled={isDisabled && !keepsFocusForTooltip}
      aria-disabled={keepsFocusForTooltip || undefined}
      aria-describedby={showReason ? reasonId : undefined}
      className={cx(
        buttonBaseClasses,
        'inline-flex items-center gap-3 px-6 text-sm font-semibold whitespace-nowrap text-primary-on-brand',
        isDisabled
          ? 'cursor-not-allowed bg-transparent text-primary-on-brand/40'
          : {
              default: 'bg-transparent data-[hovered]:bg-white/[.12] data-[pressed]:bg-white/[.2]',
              danger: 'bg-transparent data-[hovered]:bg-error-600 data-[pressed]:bg-error-700',
              primary: 'bg-brand-600 data-[hovered]:bg-brand-700 data-[pressed]:bg-brand-800',
            }[tone],
      )}
    >
      <span className="flex shrink-0" aria-hidden="true">
        {icon}
      </span>
      {label}
    </AriaButton>
  );

  return (
    <>
      {tooltip ? (
        <TooltipTrigger delay={300}>
          {button}
          <Tooltip>{tooltip}</Tooltip>
        </TooltipTrigger>
      ) : (
        button
      )}
      {showReason && (
        <span id={reasonId} className="sr-only">
          {disabledReason}
        </span>
      )}
    </>
  );
}

function Divider() {
  return <div aria-hidden="true" className="mx-3 h-9 w-px shrink-0 bg-white/[.18]" />;
}

/**
 * Floating toolbar for acting on a multi-selection (assets in a collection or the library): the selected count, the
 * actions, and a button to clear the selection. Fixed at the bottom center of the viewport, it slides up when the
 * selection starts and renders nothing while the selection is empty. Esc clears the selection.
 *
 * @example
 * <BulkActionBar
 *   count={selected.size}
 *   onClear={clearSelection}
 *   actions={[
 *     { key: 'fav', label: 'Favorite', icon: <HeartIcon size={15} />, onPress: favoriteSelected },
 *     { key: 'delete', label: 'Delete', icon: <Trash01Icon size={15} />, onPress: confirmDelete, tone: 'danger' },
 *   ]}
 * />
 */
function BulkActionBar(props: BulkActionBarProps) {
  const { count, actions, onClear, 'aria-label': ariaLabel = 'Bulk actions', className } = props;
  const isVisible = count > 0;

  React.useEffect(() => {
    if (!isVisible) {
      return;
    }

    let pendingClear: ReturnType<typeof setTimeout> | undefined;
    // Overlays (like a delete confirmation) handle their own Esc and prevent it, and Esc in a text field backs out of
    // the field; leave both alone. An open tooltip stops Esc on its way down without preventing it, so the bar listens
    // before it and decides once the event has run its course
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isEditableTarget(event.target)) return;
      pendingClear = setTimeout(() => {
        if (!event.defaultPrevented) onClear();
      });
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      clearTimeout(pendingClear);
    };
  }, [isVisible, onClear]);

  // When the bar unmounts with focus inside it, focus falls to body. After a confirmed Delete the consumer moves it
  // (the Collection grid takes it back, BLU-353). TODO: × via keyboard still drops focus to body
  if (!isVisible) {
    return null;
  }

  return (
    <Toolbar
      aria-label={ariaLabel}
      className={cx(
        // z-40: above page content, below the dialog backdrop (z-50) so dialogs like the delete confirmation cover it
        'fixed bottom-12 left-1/2 z-40 flex -translate-x-1/2 items-center gap-4 py-4 pr-4 pl-8',
        // On narrow screens the bar stays inside the viewport and the actions scroll, keeping × in reach
        'max-w-[calc(100vw-32px)]',
        'bg-foreground-primary text-primary-on-brand shadow-xl',
        'motion-safe:animate-bulk-bar-up',
        className,
      )}
    >
      <span className="shrink-0 whitespace-nowrap text-sm font-semibold">{count} selected</span>
      <Divider />
      <div className="flex min-w-0 items-center gap-4 overflow-x-auto [scrollbar-width:none]">
        {actions.map(({ key, ...action }) => (
          <BulkActionButton key={key} {...action} />
        ))}
      </div>
      <Divider />
      <span className="inline-flex shrink-0" title="Clear selection (Esc)">
        <AriaButton
          onPress={onClear}
          aria-label="Clear selection"
          className={cx(
            buttonBaseClasses,
            'grid w-16 place-items-center bg-transparent text-primary-on-brand/75',
            'data-[hovered]:bg-white/[.12] data-[pressed]:bg-white/[.2]',
          )}
        >
          <XCloseIcon size={14} aria-hidden="true" />
        </AriaButton>
      </span>
    </Toolbar>
  );
}

export { BulkActionBar };
export type { BulkAction, BulkActionBarProps };
